import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Phone, Mail, User, Send, Plus, CheckSquare, Square } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ['New', 'Urgent', 'Attempting Contact', 'Contacted', 'Qualified', 'Application Started', 'Awaiting Documents', 'Under Review', 'Bond Posted', 'Closed Won', 'Closed Lost', 'Invalid / Duplicate', 'Do Not Contact'];

const Field = ({ label, value }) => (
  <div className="detail-field">
    <span className="detail-field__label">{label}</span>
    <span className="detail-field__value">{value || <span style={{ color: 'var(--text-dim)' }}>&mdash;</span>}</span>
  </div>
);

const LeadDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [lead, setLead] = useState(null);
  const [statusReason, setStatusReason] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [noteText, setNoteText] = useState('');
  const [taskTitle, setTaskTitle] = useState('');
  const [taskDue, setTaskDue] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/leads/${id}`).then((res) => { setLead(res.data); setNewStatus(res.data.status); }).catch(() => navigate('/dashboard/leads'));
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  if (!lead) return <div className="empty-state">Loading lead...</div>;

  const updateStatus = async () => {
    if (newStatus === lead.status && !statusReason) return;
    setBusy(true);
    try {
      await axios.patch(`${API}/leads/${id}/status`, { status: newStatus, reason: statusReason });
      setStatusReason('');
      load();
    } finally { setBusy(false); }
  };

  const assignToMe = async () => {
    setBusy(true);
    try { await axios.post(`${API}/leads/${id}/assign`, { assigned_to: user.email }); load(); } finally { setBusy(false); }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    setBusy(true);
    try { await axios.post(`${API}/leads/${id}/notes`, { text: noteText }); setNoteText(''); load(); } finally { setBusy(false); }
  };

  const addTask = async () => {
    if (!taskTitle.trim()) return;
    setBusy(true);
    try { await axios.post(`${API}/leads/${id}/tasks`, { title: taskTitle, due_date: taskDue || null }); setTaskTitle(''); setTaskDue(''); load(); } finally { setBusy(false); }
  };

  const toggleTask = async (taskId) => {
    await axios.patch(`${API}/leads/${id}/tasks/${taskId}`);
    load();
  };

  return (
    <div data-testid="dash-lead-detail">
      <Link to="/dashboard/leads" className="dash-btn mb-6" style={{ display: 'inline-flex' }} data-testid="lead-detail-back"><ArrowLeft size={15} /> Back to Inbox</Link>

      <div className="dash-panel">
        <div className="dash-panel__head">
          <h2>{lead.defendant_name} <span className={`status-pill status-${lead.status.replace(/[\s/]/g, '_')}`} style={{ marginLeft: 8 }}>{lead.status}</span></h2>
          <span style={{ color: 'var(--text-dim)', fontSize: '0.8rem' }} data-testid="lead-detail-ref">{lead.id}</span>
        </div>

        <div className="detail-grid">
          <div>
            <div className="detail-fields-grid">
              <Field label="Requester" value={lead.requester_name} />
              <Field label="Callback Phone" value={<a href={`tel:${lead.callback_phone}`} style={{ color: 'var(--gold)' }}><Phone size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />{lead.callback_phone}</a>} />
              <Field label="Jail / City" value={lead.jail_city} />
              <Field label="In Custody" value={lead.in_custody ? 'Yes' : 'No'} />
              <Field label="Preferred Language" value={lead.preferred_language === 'es' ? 'Espa\u00f1ol' : 'English'} />
              <Field label="Preferred Contact" value={lead.preferred_contact} />
              <Field label="Defendant DOB" value={lead.defendant_dob} />
              <Field label="Relationship" value={lead.relation_to_defendant} />
              <Field label="Charge" value={lead.charge} />
              <Field label="Case / Booking #" value={lead.case_number} />
              <Field label="Est. Bond Amount" value={lead.bond_amount_estimate} />
              <Field label="Co-signer Ready" value={lead.cosigner_ready} />
              <Field label="Consent (Call/Text)" value={lead.consent_call_text ? 'Given' : 'Not given'} />
              <Field label="Assigned To" value={lead.assigned_to} />
              <Field label="Source" value={lead.utm_source || 'Direct'} />
              <Field label="Campaign" value={lead.utm_campaign} />
              <Field label="Device" value={lead.device_type} />
              <Field label="Submitted" value={new Date(lead.created_at).toLocaleString()} />
              <Field label="First Contact" value={lead.first_contact_at ? new Date(lead.first_contact_at).toLocaleString() : null} />
            </div>
            {lead.notes && (
              <div className="mt-6">
                <span className="detail-field__label">Submitted Notes</span>
                <p style={{ color: 'var(--text-secondary)', marginTop: 4 }}>{lead.notes}</p>
              </div>
            )}

            <div className="mt-8">
              <h3 style={{ fontFamily: 'var(--font-alt)', fontSize: '0.85rem', textTransform: 'uppercase', color: 'var(--text-primary)', marginBottom: '0.8rem' }}>Notes ({lead.notes_log?.length || 0})</h3>
              {(lead.notes_log || []).map((n) => (
                <div className="note-item" key={n.id}>
                  <div className="note-item__meta">{n.author} &middot; {new Date(n.timestamp).toLocaleString()}</div>
                  <div className="note-item__text">{n.text}</div>
                </div>
              ))}
              <div className="flex gap-2 mt-4">
                <input className="dash-input" style={{ flex: 1 }} placeholder="Add an internal note..." value={noteText} onChange={(e) => setNoteText(e.target.value)} data-testid="note-input" />
                <button className="dash-btn primary" onClick={addNote} disabled={busy} data-testid="note-submit-btn"><Send size={14} /></button>
              </div>
            </div>

            <div className="mt-8">
              <h3 style={{ fontFamily: 'var(--font-alt)', fontSize: '0.85rem', textTransform: 'uppercase', color: 'var(--text-primary)', marginBottom: '0.8rem' }}>Tasks / Follow-ups</h3>
              {(lead.tasks || []).map((tk) => (
                <div className={`task-row ${tk.done ? 'done' : ''} ${tk.overdue ? 'overdue' : ''}`} key={tk.id}>
                  <button onClick={() => toggleTask(tk.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: tk.done ? 'var(--success)' : 'var(--text-dim)' }} data-testid={`task-toggle-${tk.id}`}>
                    {tk.done ? <CheckSquare size={18} /> : <Square size={18} />}
                  </button>
                  <span className="task-row__title">{tk.title}</span>
                  {tk.due_date && <span className="task-row__due">Due {tk.due_date}</span>}
                </div>
              ))}
              <div className="flex gap-2 mt-4" style={{ flexWrap: 'wrap' }}>
                <input className="dash-input" style={{ flex: 1, minWidth: 160 }} placeholder="New task..." value={taskTitle} onChange={(e) => setTaskTitle(e.target.value)} data-testid="task-title-input" />
                <input type="date" className="dash-input" value={taskDue} onChange={(e) => setTaskDue(e.target.value)} data-testid="task-due-input" />
                <button className="dash-btn primary" onClick={addTask} disabled={busy} data-testid="task-submit-btn"><Plus size={14} /> Add</button>
              </div>
            </div>
          </div>

          <div>
            <div className="dash-panel" style={{ margin: 0 }}>
              <h3 style={{ fontFamily: 'var(--font-alt)', fontSize: '0.85rem', textTransform: 'uppercase', marginBottom: '0.8rem' }}>Update Status</h3>
              <select className="dash-select mb-4" style={{ width: '100%' }} value={newStatus} onChange={(e) => setNewStatus(e.target.value)} data-testid="status-select">
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <input className="dash-input mb-4" style={{ width: '100%' }} placeholder="Reason (optional)" value={statusReason} onChange={(e) => setStatusReason(e.target.value)} data-testid="status-reason-input" />
              <button className="dash-btn primary" style={{ width: '100%' }} onClick={updateStatus} disabled={busy} data-testid="status-update-btn">Update Status</button>

              {!lead.assigned_to && (
                <button className="dash-btn mt-4" style={{ width: '100%' }} onClick={assignToMe} disabled={busy} data-testid="assign-to-me-btn"><User size={14} /> Assign to Me</button>
              )}
            </div>

            <div className="dash-panel">
              <h3 style={{ fontFamily: 'var(--font-alt)', fontSize: '0.85rem', textTransform: 'uppercase', marginBottom: '0.8rem' }}>Status Timeline</h3>
              <div className="timeline">
                {(lead.status_history || []).slice().reverse().map((h, i) => (
                  <div className="timeline-item" key={i}>
                    <strong style={{ color: 'var(--text-primary)' }}>{h.status}</strong>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{h.user} &middot; {new Date(h.timestamp).toLocaleString()}</div>
                    {h.reason && <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginTop: 2 }}>{h.reason}</div>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadDetail;
